import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle, statCard } from '../theme';

const LEAD_STAGES = ['new', 'contacted', 'consult_booked', 'consulted', 'treatment_started', 'converted', 'lost', 'cold'];
const LEAD_SOURCES = ['walk-in', 'website', 'google', 'instagram', 'referral', 'whatsapp', 'call', 'camp'];

const emptyLead = () => ({ name:'', phone:'', email:'', source:'walk-in', serviceInterest:'', estimatedValue:'', stage:'new', assignedTo:'', nextFollowUpAt:'', notes:'' });

const MarketingPage = ({ API_BASE }) => {
  const [tab, setTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [fbSummary, setFbSummary] = useState({ total:0, avg:0, nps:0, distribution:{} });
  const [form, setForm] = useState({ name:'', channel:'whatsapp', message:'', segment:{ gender:'', lastVisitDays:'' } });
  const [preview, setPreview] = useState(null);

  // Lead state
  const [leads, setLeads] = useState([]);
  const [leadStats, setLeadStats] = useState({ total:0, active:0, converted:0, lost:0, convRate:'0.0', byStage:{} });
  const [leadForm, setLeadForm] = useState(emptyLead());
  const [leadEditingId, setLeadEditingId] = useState(null);
  const [leadFilterStage, setLeadFilterStage] = useState('');

  const fetchAll = async () => {
    try {
      const [c, f, s] = await Promise.all([
        axios.get(`${API_BASE}/marketing/campaigns`),
        axios.get(`${API_BASE}/marketing/feedback`),
        axios.get(`${API_BASE}/marketing/feedback/summary`),
      ]);
      setCampaigns(c.data); setFeedbacks(f.data); setFbSummary(s.data);
    } catch(e){}
  };

  const fetchLeads = async () => {
    try {
      const params = leadFilterStage ? { stage: leadFilterStage } : {};
      const [l, st] = await Promise.all([
        axios.get(`${API_BASE}/leads`, { params }),
        axios.get(`${API_BASE}/leads/stats`),
      ]);
      setLeads(l.data); setLeadStats(st.data);
    } catch(e){}
  };

  useEffect(() => { fetchAll(); fetchLeads(); }, []); // eslint-disable-line
  useEffect(() => { if (tab === 'leads' || tab === 'lead-add') fetchLeads(); }, [leadFilterStage]); // eslint-disable-line

  const handleLeadSave = async () => {
    if (!leadForm.name.trim()) return alert('Lead name required');
    try {
      const payload = { ...leadForm, estimatedValue: Number(leadForm.estimatedValue) || 0 };
      if (!payload.nextFollowUpAt) delete payload.nextFollowUpAt;
      if (leadEditingId) {
        await axios.put(`${API_BASE}/leads/${leadEditingId}`, payload);
      } else {
        await axios.post(`${API_BASE}/leads`, payload);
      }
      setLeadForm(emptyLead()); setLeadEditingId(null); setTab('leads'); fetchLeads();
    } catch(e){ alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const handleLeadEdit = (l) => {
    setLeadEditingId(l._id);
    setLeadForm({
      name: l.name || '', phone: l.phone || '', email: l.email || '',
      source: l.source || 'walk-in', serviceInterest: l.serviceInterest || '',
      estimatedValue: l.estimatedValue || '', stage: l.stage || 'new',
      assignedTo: l.assignedTo || '',
      nextFollowUpAt: l.nextFollowUpAt ? new Date(l.nextFollowUpAt).toISOString().slice(0,10) : '',
      notes: l.notes || '',
    });
    setTab('lead-add');
  };

  const handleLeadAdvance = async (l) => {
    const currIdx = LEAD_STAGES.indexOf(l.stage);
    const next = LEAD_STAGES[currIdx + 1];
    if (!next || ['converted','lost','cold'].includes(next)) return alert('Use Convert / Lose buttons for final stages');
    try {
      await axios.post(`${API_BASE}/leads/${l._id}/advance`, { toStage: next, note: `Moved to ${next}` });
      fetchLeads();
    } catch(e){ alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const handleLeadLose = async (l) => {
    const reason = window.prompt('Reason for losing this lead?');
    if (reason === null) return;
    try {
      await axios.post(`${API_BASE}/leads/${l._id}/lose`, { reason });
      fetchLeads();
    } catch(e){ alert('Failed'); }
  };

  const handleLeadDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try { await axios.delete(`${API_BASE}/leads/${id}`); fetchLeads(); } catch(e){ alert('Failed'); }
  };

  const handlePreview = async () => {
    try {
      const r = await axios.post(`${API_BASE}/marketing/campaigns/preview`, { segment: form.segment });
      setPreview(r.data);
    } catch(e){ setPreview(null); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return alert('Campaign name required');
    try {
      await axios.post(`${API_BASE}/marketing/campaigns`, {
        ...form, recipientCount: preview?.count || 0
      });
      setForm({ name:'', channel:'whatsapp', message:'', segment:{ gender:'', lastVisitDays:'' } });
      setPreview(null); setTab('campaigns'); fetchAll();
    } catch(e){ alert('Failed'); }
  };

  const handleSend = async (id) => {
    if (!window.confirm('Send this campaign now?')) return;
    await axios.post(`${API_BASE}/marketing/campaigns/${id}/send`);
    fetchAll();
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

  const tabBtn = (key, label) => (
    <button onClick={()=>setTab(key)}
      style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
        backgroundColor: tab===key ? theme.primary : '#e9ecef', color: tab===key ? 'white' : '#555'}}>
      {label}
    </button>
  );

  const stars = (n) => '★'.repeat(n) + '☆'.repeat(5-n);

  return (
    <div>
      {/* KPI */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px', marginBottom:'20px'}}>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Campaigns</span>
          <h2 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>{campaigns.length}</h2></div>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Avg Rating</span>
          <h2 style={{margin:'4px 0 0', color:theme.warning, fontSize:'1.4rem', fontWeight:'700'}}>{fbSummary.avg} / 5</h2></div>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>NPS Score</span>
          <h2 style={{margin:'4px 0 0', color: fbSummary.nps >= 50 ? theme.success : fbSummary.nps >= 0 ? theme.warning : theme.danger, fontSize:'1.4rem', fontWeight:'700'}}>{fbSummary.nps}</h2></div>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Total Feedback</span>
          <h2 style={{margin:'4px 0 0', color:theme.info, fontSize:'1.4rem', fontWeight:'700'}}>{fbSummary.total}</h2></div>
      </div>

      <div style={{display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap'}}>
        {tabBtn('campaigns', 'Campaigns')}
        {tabBtn('create', '+ New Campaign')}
        {tabBtn('feedback', 'Patient Feedback')}
        {tabBtn('leads', `Leads (${leadStats.active || 0})`)}
        {tabBtn('lead-add', leadEditingId ? 'Edit Lead' : '+ New Lead')}
      </div>

      {/* Campaigns List */}
      {tab === 'campaigns' && (
        <div style={cardStyle}>
          {campaigns.length === 0 ? <p style={{color:'#aaa', textAlign:'center', padding:'40px'}}>No campaigns yet</p> : (
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={thStyle}>Name</th><th style={thStyle}>Channel</th><th style={thStyle}>Recipients</th>
                <th style={thStyle}>Sent</th><th style={thStyle}>Status</th><th style={thStyle}>Date</th><th style={thStyle}></th>
              </tr></thead>
              <tbody>{campaigns.map(c => (
                <tr key={c._id}>
                  <td style={tdStyle}><strong>{c.name}</strong></td>
                  <td style={tdStyle}>{c.channel}</td>
                  <td style={tdStyle}>{c.recipientCount}</td>
                  <td style={tdStyle}>{c.sentCount}</td>
                  <td style={tdStyle}>
                    <span style={{padding:'3px 10px', borderRadius:'12px', fontSize:'0.74rem', fontWeight:'700',
                      backgroundColor: c.status==='sent'?'#d1fae5':'#fef3c7',
                      color: c.status==='sent'?'#065f46':'#92400e'}}>{c.status}</span>
                  </td>
                  <td style={tdStyle}>{fmtDate(c.sentAt || c.createdAt)}</td>
                  <td style={tdStyle}>
                    {c.status === 'draft' && <button onClick={()=>handleSend(c._id)}
                      style={{padding:'4px 10px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Send</button>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* Create Campaign */}
      {tab === 'create' && (
        <div style={{...cardStyle, borderTop:`4px solid ${theme.primary}`}}>
          <h3 style={{margin:'0 0 16px', color:theme.navy}}>Create Campaign</h3>
          <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px', marginBottom:'14px'}}>
            <div><label style={labelStyle}>Campaign Name *</label><input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Channel</label>
              <select value={form.channel} onChange={e=>setForm({...form, channel:e.target.value})} style={inputStyle}>
                <option value="whatsapp">WhatsApp</option><option value="sms">SMS</option><option value="email">Email</option>
              </select>
            </div>
            <div style={{gridColumn:'span 2'}}><label style={labelStyle}>Message</label>
              <textarea value={form.message} onChange={e=>setForm({...form, message:e.target.value})} rows={3} style={{...inputStyle, resize:'vertical'}}/></div>
          </div>
          <h4 style={{color:theme.navy, margin:'0 0 12px'}}>Segment Filters</h4>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'14px'}}>
            <div><label style={labelStyle}>Gender</label>
              <select value={form.segment.gender} onChange={e=>setForm({...form, segment:{...form.segment, gender:e.target.value}})} style={inputStyle}>
                <option value="">All</option><option>Male</option><option>Female</option>
              </select>
            </div>
            <div><label style={labelStyle}>Not visited in (days)</label>
              <input type="number" value={form.segment.lastVisitDays} onChange={e=>setForm({...form, segment:{...form.segment, lastVisitDays:e.target.value}})} placeholder="e.g. 90" style={inputStyle}/></div>
            <div style={{display:'flex', alignItems:'flex-end'}}>
              <button onClick={handlePreview} style={{...btnStyle, marginTop:0, backgroundColor:'#6c757d'}}>Preview Recipients</button>
            </div>
          </div>
          {preview && <p style={{color:theme.info, fontWeight:'600', marginBottom:'14px'}}>Matching patients: {preview.count}</p>}
          <button onClick={handleCreate} style={{...btnStyle, marginTop:0}}>Create Campaign</button>
        </div>
      )}

      {/* Feedback */}
      {/* Leads List */}
      {tab === 'leads' && (
        <div style={cardStyle}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px', marginBottom:'16px'}}>
            <div style={{...statCard, padding:'10px'}}><span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Total</span><h3 style={{margin:'2px 0 0', color:theme.navy, fontSize:'1.2rem'}}>{leadStats.total}</h3></div>
            <div style={{...statCard, padding:'10px'}}><span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Active</span><h3 style={{margin:'2px 0 0', color:theme.info, fontSize:'1.2rem'}}>{leadStats.active}</h3></div>
            <div style={{...statCard, padding:'10px'}}><span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Converted</span><h3 style={{margin:'2px 0 0', color:theme.success, fontSize:'1.2rem'}}>{leadStats.converted}</h3></div>
            <div style={{...statCard, padding:'10px'}}><span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Lost</span><h3 style={{margin:'2px 0 0', color:theme.danger, fontSize:'1.2rem'}}>{leadStats.lost}</h3></div>
            <div style={{...statCard, padding:'10px'}}><span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Conv. Rate</span><h3 style={{margin:'2px 0 0', color:theme.primary, fontSize:'1.2rem'}}>{leadStats.convRate}%</h3></div>
          </div>
          <div style={{marginBottom:'12px', display:'flex', gap:'10px', alignItems:'center'}}>
            <label style={{...labelStyle, margin:0}}>Filter Stage:</label>
            <select value={leadFilterStage} onChange={e=>setLeadFilterStage(e.target.value)} style={{...inputStyle, width:'auto', minWidth:'160px'}}>
              <option value="">All Stages</option>
              {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {leads.length === 0 ? <p style={{color:'#aaa', textAlign:'center', padding:'40px'}}>No leads yet</p> : (
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={thStyle}>Name</th><th style={thStyle}>Phone</th><th style={thStyle}>Source</th>
                <th style={thStyle}>Interest</th><th style={thStyle}>Value</th><th style={thStyle}>Stage</th>
                <th style={thStyle}>Assigned</th><th style={thStyle}>Next F/U</th><th style={thStyle}>Actions</th>
              </tr></thead>
              <tbody>{leads.map(l => (
                <tr key={l._id}>
                  <td style={tdStyle}><strong>{l.name}</strong></td>
                  <td style={tdStyle}>{l.phone || '—'}</td>
                  <td style={tdStyle}>{l.source}</td>
                  <td style={tdStyle}>{l.serviceInterest || '—'}</td>
                  <td style={tdStyle}>{l.estimatedValue ? `₹${l.estimatedValue}` : '—'}</td>
                  <td style={tdStyle}>
                    <span style={{padding:'3px 8px', borderRadius:'10px', fontSize:'0.72rem', fontWeight:'700',
                      backgroundColor: l.stage==='converted'?'#d1fae5':l.stage==='lost'?'#fee2e2':l.stage==='new'?'#dbeafe':'#fef3c7',
                      color: l.stage==='converted'?'#065f46':l.stage==='lost'?'#991b1b':l.stage==='new'?'#1e40af':'#92400e'}}>{l.stage}</span>
                  </td>
                  <td style={tdStyle}>{l.assignedTo || '—'}</td>
                  <td style={tdStyle}>{l.nextFollowUpAt ? fmtDate(l.nextFollowUpAt) : '—'}</td>
                  <td style={tdStyle}>
                    <div style={{display:'flex', gap:'4px', flexWrap:'wrap'}}>
                      {!['converted','lost','cold'].includes(l.stage) && (
                        <button onClick={()=>handleLeadAdvance(l)} style={{padding:'3px 8px', backgroundColor:theme.info, color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem'}}>→ Next</button>
                      )}
                      <button onClick={()=>handleLeadEdit(l)} style={{padding:'3px 8px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem'}}>Edit</button>
                      {!['converted','lost'].includes(l.stage) && (
                        <button onClick={()=>handleLeadLose(l)} style={{padding:'3px 8px', backgroundColor:theme.warning, color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem'}}>Lost</button>
                      )}
                      <button onClick={()=>handleLeadDelete(l._id)} style={{padding:'3px 8px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem'}}>×</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* Lead Add/Edit Form */}
      {tab === 'lead-add' && (
        <div style={{...cardStyle, borderTop:`4px solid ${theme.primary}`}}>
          <h3 style={{margin:'0 0 16px', color:theme.navy}}>{leadEditingId ? 'Edit Lead' : 'New Lead'}</h3>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'14px'}}>
            <div><label style={labelStyle}>Name *</label><input value={leadForm.name} onChange={e=>setLeadForm({...leadForm, name:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Phone</label><input value={leadForm.phone} onChange={e=>setLeadForm({...leadForm, phone:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Email</label><input value={leadForm.email} onChange={e=>setLeadForm({...leadForm, email:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Source</label>
              <select value={leadForm.source} onChange={e=>setLeadForm({...leadForm, source:e.target.value})} style={inputStyle}>
                {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Service Interest</label><input value={leadForm.serviceInterest} onChange={e=>setLeadForm({...leadForm, serviceInterest:e.target.value})} placeholder="Aligners, Implants..." style={inputStyle}/></div>
            <div><label style={labelStyle}>Estimated Value (₹)</label><input type="number" value={leadForm.estimatedValue} onChange={e=>setLeadForm({...leadForm, estimatedValue:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Stage</label>
              <select value={leadForm.stage} onChange={e=>setLeadForm({...leadForm, stage:e.target.value})} style={inputStyle}>
                {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Assigned To</label><input value={leadForm.assignedTo} onChange={e=>setLeadForm({...leadForm, assignedTo:e.target.value})} placeholder="staff username" style={inputStyle}/></div>
            <div><label style={labelStyle}>Next Follow-up</label><input type="date" value={leadForm.nextFollowUpAt} onChange={e=>setLeadForm({...leadForm, nextFollowUpAt:e.target.value})} style={inputStyle}/></div>
            <div style={{gridColumn:'span 3'}}><label style={labelStyle}>Notes</label>
              <textarea value={leadForm.notes} onChange={e=>setLeadForm({...leadForm, notes:e.target.value})} rows={2} style={{...inputStyle, resize:'vertical'}}/></div>
          </div>
          <div style={{display:'flex', gap:'8px'}}>
            <button onClick={handleLeadSave} style={{...btnStyle, marginTop:0}}>{leadEditingId ? 'Update Lead' : 'Create Lead'}</button>
            {leadEditingId && (
              <button onClick={()=>{ setLeadEditingId(null); setLeadForm(emptyLead()); setTab('leads'); }}
                style={{...btnStyle, marginTop:0, backgroundColor:'#6c757d'}}>Cancel</button>
            )}
          </div>
        </div>
      )}

      {tab === 'feedback' && (
        <div style={cardStyle}>
          <div style={{display:'flex', gap:'20px', marginBottom:'20px'}}>
            {[5,4,3,2,1].map(n => (
              <div key={n} style={{textAlign:'center'}}>
                <div style={{fontSize:'1.2rem', color:theme.warning}}>{stars(n).slice(0,n)}</div>
                <div style={{fontSize:'1.1rem', fontWeight:'700', color:theme.navy}}>{fbSummary.distribution?.[n] || 0}</div>
              </div>
            ))}
          </div>
          {feedbacks.length === 0 ? <p style={{color:'#aaa', textAlign:'center', padding:'30px'}}>No feedback yet</p> : (
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={thStyle}>Patient</th><th style={thStyle}>Rating</th><th style={thStyle}>Comment</th>
                <th style={thStyle}>Source</th><th style={thStyle}>Date</th>
              </tr></thead>
              <tbody>{feedbacks.map(f => (
                <tr key={f._id}>
                  <td style={tdStyle}><strong>{f.patientId?.name||'—'}</strong></td>
                  <td style={tdStyle}><span style={{color:theme.warning}}>{stars(f.rating)}</span></td>
                  <td style={tdStyle}>{f.comment || '—'}</td>
                  <td style={tdStyle}>{f.source}</td>
                  <td style={tdStyle}>{fmtDate(f.createdAt)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketingPage;
